import { voiceEvents } from '../../content/voices/voice-events.js';
import { voicePacks, voicePackFor } from '../../content/voices/voice-packs.js';
import { voiceSpaceFor, type VoiceSpace } from '../../content/voices/voice-spaces.js';
import type { HeroDefinition } from '../../shared/game/world-types.js';
import type { ClientMessage, WorldSnapshot } from '../../shared/network/protocol.js';
import { AutoVoices } from './auto-voices.js';
import { voiceForCommand } from './command-voices.js';
import { mixer } from './audio-mixer.js';
import { groupClips, VoiceDirector, type VoiceClips, type VoicePlacement } from './voice-director.js';

const files = import.meta.glob('../assets/audio/dialogue/hero voices/*/*.{mp3,wav,flac,ogg}', { eager: true, query: '?url', import: 'default' }) as Record<string, string>;
const folders = groupClips(files);
const clipsOf = (packId: string): VoiceClips | undefined => folders.get(voicePacks.find(pack => pack.id === packId)?.folder ?? '');

let placement: (heroId: string) => VoicePlacement | null = () => null;
/** The room each hero's ancestry speaks in (`voice-spaces.ts`); a hero not listed speaks dry. */
const spaces = new Map<string, VoiceSpace>();
/** The room the creator's Listen button plays in. */
let previewSpace: VoiceSpace | undefined;
const treatments = new Map(voiceEvents.flatMap(event => event.treatment ? [[event.id, event.treatment] as const] : []));
/** Tells the voices where a hero stands on screen (the scene's `voicePlacement`), so they come from the hero. */
export function setVoicePlacement(place: (heroId: string) => VoicePlacement | null) { placement = place; }

/** The player's heroes talking back: commands, refusals and signature powers speak through this. Odds and packs live in `src/content/voices`. */
export const heroVoice = new VoiceDirector({
  events: voiceEvents,
  clips: clipsOf,
  play: (url, at, heroId, eventId) => {
    const treatment = eventId ? treatments.get(eventId) : undefined;
    return mixer.playVoice(url, at?.strength ?? 1, at?.pan ?? 0, treatment?.space ?? (heroId ? spaces.get(heroId) : previewSpace), treatment?.rate);
  },
  stop: () => mixer.stopVoice(),
  place: heroId => placement(heroId),
  frequency: () => mixer.voiceFrequency,
});

/** Gives each hero their pack and decodes the packs in use, so the first line doesn't wait on a download. */
export function prepareVoices(heroes: HeroDefinition[]) {
  const used = new Set<string>();
  for (const hero of heroes) {
    const pack = voicePackFor(hero);
    heroVoice.assign(hero.id, pack);
    const space = voiceSpaceFor(hero.ancestryId ?? hero.appearance?.ancestry);
    if (space) spaces.set(hero.id, space); else spaces.delete(hero.id);
    if (pack) used.add(pack);
  }
  const urls = [...used].flatMap(pack => [...(clipsOf(pack)?.values() ?? [])].flat());
  if (urls.length) void mixer.preloadSfx(urls);
}

/** Plays a sample line of a pack (the character creator's Listen button), in the room of the ancestry being made. */
export function previewVoice(packId: string, ancestryId?: string) {
  const urls = [...(clipsOf(packId)?.values() ?? [])].flat();
  if (urls.length) void mixer.preloadSfx(urls);
  previewSpace = voiceSpaceFor(ancestryId);
  heroVoice.sample(packId);
}

/** A hero answers an event if they are `owned` (when given) and alive; one of those named speaks, odds and cooldowns permitting. */
export function speak(heroIds: string[], event: string, snapshot: WorldSnapshot | null, owned?: string[], options: { force?: boolean; auto?: boolean } = {}) {
  return heroVoice.say(heroIds.filter(id => (!owned || owned.includes(id)) && !snapshot?.heroes.find(hero => hero.id === id)?.health.dead), event, options);
}
/** Heroes speaking for what they decide to do under their behavior settings: feed it every snapshot, with the heroes this player controls. */
export const autoVoices = new AutoVoices((heroId, event) => void heroVoice.say([heroId], event, { auto: true }), undefined, heroId => placement(heroId) !== null);
/** Speaks for a command the player just sent, if it calls for a voice line. */
export function speakCommand(message: ClientMessage, snapshot: WorldSnapshot | null, owned?: string[]) {
  const voiced = voiceForCommand(message, snapshot);
  if (voiced && (message.type === 'ability' || message.type === 'use-item')) autoVoices.manual(voiced.heroIds);
  if (voiced) speak(voiced.heroIds, voiced.event, snapshot, owned);
}
